import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { listCourses, type CourseListItem } from "../api/courses";
import { createAssignment } from "../api/assignments";
import { ApiError } from "../api/client";
import { moscowDateToUtcIso } from "../utils/datetime";

interface Props {
  userIds: number[];
  onClose: () => void;
  onDone: (created: number, skipped: number) => void;
}

export function BulkAssignDialog({ userIds, onClose, onDone }: Props) {
  const [courses, setCourses] = useState<CourseListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [courseId, setCourseId] = useState<number | "">("");
  const [dueAt, setDueAt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    void listCourses({ page_size: 100 })
      .then((r) => {
        const usable = r.items.filter((c) => c.status !== "archived");
        setCourses(usable);
        if (usable.length > 0) setCourseId(usable[0]!.id);
      })
      .catch((err) =>
        setError(err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна"),
      )
      .finally(() => setLoading(false));
  }, []);

  async function handleSubmit() {
    if (typeof courseId !== "number") return;
    setSubmitting(true);
    setError(null);
    setProgress(0);
    let created = 0;
    let skipped = 0;
    for (const uid of userIds) {
      try {
        await createAssignment(courseId, {
          target_type: "user",
          target_user_id: uid,
          due_at: moscowDateToUtcIso(dueAt),
        });
        created++;
      } catch {
        skipped++;
      }
      setProgress((p) => p + 1);
    }
    setSubmitting(false);
    onDone(created, skipped);
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/50 p-4">
      <div className="card w-full max-w-md p-5">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-ink">Назначить курс</h2>
            <p className="mt-0.5 text-xs text-ink-muted">
              Выбрано пользователей: {userIds.length}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="grid h-8 w-8 place-items-center rounded-lg text-ink-soft transition hover:bg-surface-alt"
            aria-label="Закрыть"
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        {error && (
          <div className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="py-8 text-center text-sm text-ink-muted">Загрузка курсов…</div>
        ) : courses.length === 0 ? (
          <div className="py-8 text-center text-sm text-ink-muted">
            Нет доступных курсов.
          </div>
        ) : (
          <div className="space-y-4">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                Курс
              </span>
              <select
                value={courseId}
                onChange={(e) => setCourseId(Number(e.target.value))}
                disabled={submitting}
                className="input mt-1.5 w-full"
              >
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                    {c.status === "draft" ? " [черновик]" : ""}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                Дедлайн (необязательно)
              </span>
              <input
                type="date"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
                disabled={submitting}
                className="input mt-1.5 w-full"
              />
            </label>

            {submitting && (
              <div>
                <div className="mb-1 text-xs text-ink-muted">
                  Назначаем… {progress} из {userIds.length}
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-alt">
                  <div
                    className="h-full bg-brand transition-all"
                    style={{ width: `${(progress / userIds.length) * 100}%` }}
                  />
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="btn-secondary"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || typeof courseId !== "number"}
                className="btn-primary"
              >
                {submitting ? "Назначаем…" : `Назначить ${userIds.length}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
